import { css } from '@emotion/react';
import { useContext, useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import moment from 'moment';
import t from 'react-translate';
import { useAppDispatch } from 'redux/store';
import { wrapThunkAction } from 'redux/utils';
import { AngularContext } from 'react-app';
import MentoringProgramContext from 'athena/components/mentoring-program/context';
import DefaultHeaderBackground from 'athena/components/program-header/default-header-background';
import useMentoringProgramRole from 'athena/hooks/use-mentoring-program-role';
import {
  createMentoringProgramRecruitmentApplication,
  deleteMentoringProgramRecruitmentApplication,
  getCurrentMentoringProgramRecruitmentApplication,
  getMentoringProgramRecruitment,
} from 'redux/actions/mentoring-program-recruitments';
import { openConfirmationDialog } from 'redux/actions/confirmation-dialogs';
import { Recruitment, RecruitmentApplication } from 'redux/schemas/models/mentoring-program-recruitments';
import { white } from 'styles/global_defaults/colors';
import { doubleSpacing, standardSpacing } from 'styles/global_defaults/scaffolding';
import FlyerHeader from './flyer-header';
import FlyerInfo from './flyer-info';
import ExpressInterestSuccessModal from './express-interest-success-modal';
import { config } from '@config/pendo.config.json';


const flyerContainerStyles = css`
  position: relative;
  background-color: ${white};
  max-width: 980px;
  width: calc(100% - ${doubleSpacing}px);
  height: fit-content;
  margin: 80px auto ${standardSpacing}px;
  border-radius: ${standardSpacing}px;
`;

const Flyer = () => {
  const dispatch = useAppDispatch();

  const { injectServices } = useContext(AngularContext);
  const [$state] = injectServices(['$state']);
  const { programId, recruitmentId } = $state.params || {};

  const { isAdmin } = useMentoringProgramRole();
  const { mentoringProgram } = useContext(MentoringProgramContext);
  const { isEnrolled } = mentoringProgram || {};

  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [currentApplication, setCurrentApplication] = useState<RecruitmentApplication | null>(null);
  const [currentRecruitment, setCurrentRecruitment] = useState<Recruitment | null>(null);

  const { isMentee } = useMentoringProgramRole(currentRecruitment?.role?.permission);

  const formattedInterestDeadline = currentRecruitment?.interestDeadline
    ? moment(currentRecruitment.interestDeadline).format('MMMM D, YYYY')
    : '';

  useEffect(() => {
    wrapThunkAction<RecruitmentApplication | null>(dispatch(getCurrentMentoringProgramRecruitmentApplication({
      programId,
      recruitmentId,
    }))).then(({ payload }) => {
      setCurrentApplication(payload);
    });
  }, []);

  useEffect(() => {
    wrapThunkAction<Recruitment>(dispatch(getMentoringProgramRecruitment({
      programId,
      recruitmentId,
    }))).then(({ payload }) => {
      setCurrentRecruitment(payload);
    });
  }, []);

  const handleWithdrawInterest = () => {
    wrapThunkAction(dispatch(deleteMentoringProgramRecruitmentApplication({
      programId,
      recruitmentId,
    }))).then(() => {
      setCurrentApplication(null);
    });
  };

  const handleClick = () => {
    if (isEnrolled) {
      $state.go('mentoring-program-root', { programId });
    } else if (currentApplication) {
      dispatch(openConfirmationDialog({
        bodyText: t.MENTORING_PROGRAMS.FLYER.WITHDRAW_INTEREST_WARNING(),
        confirmText: t.MENTORING_PROGRAMS.FLYER.BUTTON.WITHDRAW(),
        onConfirm: handleWithdrawInterest,
      }));
    } else {
      wrapThunkAction<RecruitmentApplication>(dispatch(createMentoringProgramRecruitmentApplication({
        programId,
        recruitmentId,
      }))).then(({ payload }) => {
        setCurrentApplication(payload);
        setShowSuccessModal(true);
      });
    }
  };

  let buttonText = t.MENTORING_PROGRAMS.FLYER.BUTTON.EXPRESS();
  let buttonDataQA = config.pendo.athena.mentorshipProgram.flyer.expressInterestButton;
  if (isEnrolled) {
    buttonText = t.MENTORING_PROGRAMS.FLYER.BUTTON.ENTER();
    buttonDataQA = config.pendo.athena.mentorshipProgram.flyer.enterProgramButton;
  } else if (currentApplication) {
    buttonText = t.MENTORING_PROGRAMS.FLYER.BUTTON.WITHDRAW();
    buttonDataQA = config.pendo.athena.mentorshipProgram.flyer.withdrawInterestButton;
  }

  return (
    <div className='w-100 bg-color-almost-white'>
      <DefaultHeaderBackground
        headerBackground={currentRecruitment?.mentorshipProgram?.headerBackground}
        extraStyles={
          css`
            position: absolute;
            width: 100%;
          `
        }
      />
      <div css={flyerContainerStyles}>
        <FlyerHeader program={currentRecruitment?.mentorshipProgram} isMentee={!!isMentee} />
        <div className='d-flex align-items-center justify-content-between p-5 border-top border-gray-6'>
          <div>
            <div className='heading-3'>{t.MENTORING_PROGRAMS.FLYER.READY_TO_MAKE_AN_IMPACT()}</div>
            {formattedInterestDeadline && (
              <div className='text-small'>{t.MENTORING_PROGRAMS.FLYER.INTEREST_DEADLINE.DEFAULT(formattedInterestDeadline)}</div>
            )}
          </div>
          <Button
            variant='primary'
            size='lg'
            onClick={handleClick}
            disabled={isAdmin}
            data-qa={buttonDataQA}
          >
            {buttonText}
          </Button>
        </div>
        <FlyerInfo recruitment={currentRecruitment} />
        {showSuccessModal && (
          <ExpressInterestSuccessModal
            onClose={() => setShowSuccessModal(false)}
            programName={currentRecruitment?.mentorshipProgram?.name || ''}
          />
        )}
      </div>
    </div>
  );
};

export default Flyer;
